/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Pulse › Asks (strategy 9.2): the table of intents from GET /v1/admin/pulse/asks, sortable by every column and paged by
 * cursor; a row's drawer (GET /asks/:intentKey) with the outcome split, the reasons, the tools and their health at the
 * time, three scrubbed sample asks and answers, the comments and the problem files; Show records lists the records
 * behind the intent (GET /records), each opening its own drawer (GET /records/:askId). `?intent=`, `?records=` and
 * `?record=` in the address are where the other pages' links land.
 */
import { useState } from 'react';
import { ArrowLeft, Check, Copy, MessagesSquare, Search } from 'lucide-react';
import { api } from '../../lib/api';
import {
  STORED_TEXT_NOTE, bundleFootText, bundleText, bundleTitle, countRows, domainLabel, durationBucketText, healthChip, outcomeChip, pctText, pulseExportHref, pulseHref, reasonLabel, sortParam, subjectName,
  surfaceLabel, toggleSort, usdText, versionsText, whenText,
  type PulseFailureBundle, type PulseIntentDetail, type PulseIntentRow, type PulseRecord, type SortState,
} from '../../lib/pulse';
import { Button, Drawer, EmptyState, Icon, Input, PageHeader, Panel, Skeleton, Sparkline, Stack, StatusChip, Table, Tag, type Column } from '../../ui';
import { count } from '../../ui/format';
import { AnonymousFoot, Chip, DEFAULT_FILTERS, ExportButton, Facts, FilterBar, PageLink, PulseError, filterParams, useLoad, usePagedList, useQueryParam, type PulseFilters } from './common';

const DEFAULT_SORT: SortState = { key: 'asks', direction: 'desc' };
const TEXT_COLUMNS = new Set(['intent', 'domain']);

export function PulseAsks() {
  const [filters, setFilters] = useState<PulseFilters>(DEFAULT_FILTERS);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [intentKey, setIntentKey] = useQueryParam('intent');
  const [recordsOf, setRecordsOf] = useQueryParam('records');
  const [recordId, setRecordId] = useQueryParam('record');
  const [outcome] = useQueryParam('outcome');

  const params = { ...filterParams(filters), q: q.trim() || undefined, sort: sortParam(sort) };
  const list = usePagedList((cursor) => api.pulseAsks({ ...params, cursor, limit: 50 }), JSON.stringify(params));

  const columns: Array<Column<PulseIntentRow>> = [
    { key: 'intent', header: 'Intent', sortable: true, cell: (row) => row.intent },
    { key: 'domain', header: 'Domain', sortable: true, hideOnPhone: true, cell: (row) => domainLabel(row.domain) },
    { key: 'asks', header: 'Asks', numeric: true, sortable: true, cell: (row) => count(row.asks) },
    { key: 'helpedPct', header: 'Helped', numeric: true, sortable: true, cell: (row) => pctText(row.helpedPct) },
    { key: 'couldNotPct', header: 'Could not', numeric: true, sortable: true, cell: (row) => pctText(row.couldNotPct) },
    { key: 'repeatRate', header: 'Repeat', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => pctText(row.repeatRate * 100) },
    { key: 'trend', header: 'Trend', hideOnPhone: true, cell: (row) => <Sparkline values={row.trend} label={`${row.intent}: asks per day, last ${row.trend.length} days`} tone={row.helpedPct < 50 ? 'bad' : 'data'} /> },
    { key: 'feedbackScore', header: 'Feedback', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => (row.feedbackScore === null ? '—' : row.feedbackScore.toFixed(1)) },
    { key: 'lastSeen', header: 'Last seen', numeric: true, sortable: true, cell: (row) => whenText(row.lastSeen) },
  ];

  return (
    <>
      <PageHeader
        meta={<span>Every intent people ask for, with how often Gyro helped. Anonymous: no names, no devices.</span>}
        actions={(
          <>
            <FilterBar filters={filters} onChange={setFilters} />
            <ExportButton href={pulseExportHref('records', { ...params, intentKey: recordsOf ?? undefined, outcome: outcome ?? undefined })} />
          </>
        )}
      />
      <div className="ui-page">
        <Stack>
          {recordsOf || outcome ? (
            <RecordsPanel intentKey={recordsOf} outcome={outcome} filters={filters} onBack={() => setRecordsOf(null)} onOpen={(askId) => setRecordId(askId)} />
          ) : (
            <Panel
              flush
              title="Intents"
              description={list.load.state === 'ready' ? `${count(list.total)} intents in the last ${filters.days} days` : 'What people ask Gyro, grouped by intent'}
              actions={<Input size="sm" prefix={<Icon icon={Search} size="sm" />} aria-label="Search intents" placeholder="Search intents…" value={q} onChange={(event) => setQ(event.target.value)} />}
              footer={<span>{STORED_TEXT_NOTE}</span>}
            >
              {list.load.state === 'failed' ? <div className="ui-panel-pad"><PulseError title="The intents could not be loaded" error={list.load.error} onRetry={list.reload} retrying={list.reloading} /></div> : (
                <Table
                  label="Intents"
                  columns={columns}
                  rows={list.items}
                  rowKey={(row) => row.intentKey}
                  loading={list.load.state === 'loading'}
                  skeletonRows={8}
                  selectedKey={intentKey}
                  onRowClick={(row) => setIntentKey(row.intentKey, { replace: false })}
                  sort={{ key: sort.key, direction: sort.direction, onSort: (key) => setSort(toggleSort(sort, key, !TEXT_COLUMNS.has(key))) }}
                  more={{ cursor: list.next, loading: list.moreLoading, onMore: list.loadMore }}
                  empty={<EmptyState compact icon={MessagesSquare} title={q ? 'No intent matches that search' : 'No asks yet'}>{q ? 'Try another word.' : 'Intents appear here as people ask Gyro with the pulse flag on.'}</EmptyState>}
                />
              )}
              {list.moreError ? <div className="ui-panel-pad ui-panel-pad--top"><PulseError title="The next page could not be loaded" error={list.moreError} /></div> : null}
            </Panel>
          )}
        </Stack>
      </div>
      <IntentDrawer intentKey={intentKey} filters={filters} onClose={() => setIntentKey(null)} onShowRecords={() => { if (intentKey) { setRecordsOf(intentKey, { replace: false }); setIntentKey(null); } }} />
      <RecordDrawer askId={recordId} onClose={() => setRecordId(null)} />
    </>
  );
}

/** The intent's drawer: the outcome split, the reasons, the tools with their health, the samples, the comments, the problems. */
function IntentDrawer({ intentKey, filters, onClose, onShowRecords }: { intentKey: string | null; filters: PulseFilters; onClose: () => void; onShowRecords: () => void }) {
  const params = filterParams(filters);
  const { load, reload, reloading } = useLoad<PulseIntentDetail | null>(() => (intentKey ? api.pulseIntent(intentKey, params) : Promise.resolve(null)), `${intentKey ?? ''}|${JSON.stringify(params)}`);
  const detail = load.state === 'ready' ? load.data : null;
  return (
    <Drawer
      open={intentKey !== null}
      onClose={onClose}
      title={detail ? detail.intent : 'Intent'}
      description={detail ? `${domainLabel(detail.domain)} · ${count(detail.asks)} asks in the last ${filters.days} days` : 'Loading…'}
      className="ui-drawer--wide"
      footer={<><Button variant="primary" onClick={onShowRecords}>Show records</Button><AnonymousFoot what="intent" /></>}
    >
      {load.state === 'loading' ? <div className="ui-form" aria-busy="true"><Skeleton size="lg" /><Skeleton size="lg" /><Skeleton size="xl" /></div> : null}
      {load.state === 'failed' ? <PulseError title="The intent could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /> : null}
      {detail ? (
        <div className="ui-form">
          <Facts rows={[
            ['Outcomes', <span className="ui-chips">{countRows(detail.outcomes as Record<string, number>).map((row) => <StatusChip key={row.key} tone={outcomeChip(row.key).tone}>{outcomeChip(row.key).label} {count(row.count)}</StatusChip>)}</span>],
            ['Why it couldn’t', countRows(detail.reasons).length ? <span className="ui-chips">{countRows(detail.reasons).map((row) => <StatusChip key={row.key} tone="neutral">{reasonLabel(row.key)} {count(row.count)}</StatusChip>)}</span> : 'no refusals or failures'],
            ['Tools', detail.tools.length ? (
              <ul className="ui-list">
                {detail.tools.map((tool) => <li key={tool.subjectId}><PageLink href={pulseHref.subject(tool.subjectId)}><code>{subjectName(tool.subjectId).name}</code></PageLink> · {count(tool.calls)} calls · {pctText(tool.okPct)} ok · <Chip spec={healthChip(tool.state)} /></li>)}
              </ul>
            ) : 'none: answered from the conversation'],
            ['Problems', detail.problemIds.length ? <span className="pulse-links">{detail.problemIds.map((id) => <PageLink key={id} href={pulseHref.problem(id)}>{id}</PageLink>)}</span> : 'none open'],
          ]} />
          <div className="ui-section">
            <h3 className="ui-section-title">Sample asks (scrubbed)</h3>
            {detail.samples.length === 0 ? <p className="ui-para ui-quiet">No sample text is kept for this intent.</p> : detail.samples.map((sample) => (
              <div key={sample.askId} className="ui-form">
                <p className="pulse-text"><strong>Ask:</strong> {sample.questionText ?? '(text erased)'}</p>
                <p className="pulse-text"><strong>Answer:</strong> {sample.answerText ?? '(text erased or not kept)'}</p>
                <p className="pulse-foot"><Chip spec={outcomeChip(sample.outcome)} /> · {whenText(sample.ts)} · {surfaceLabel(sample.surface)} · <PageLink href={pulseHref.record(sample.askId)}>Open the record</PageLink></p>
              </div>
            ))}
          </div>
          <div className="ui-section">
            <h3 className="ui-section-title">Comments</h3>
            {detail.comments.length === 0 ? <p className="ui-para ui-quiet">No comments on this intent.</p> : detail.comments.map((comment) => (
              <blockquote key={comment.key} className="pulse-quote">{`“${comment.comment}”`} <span className="pulse-foot">{whenText(comment.atMs)}{comment.undo ? ' · had to undo it' : ''} · <PageLink href={pulseHref.feedback(comment.key)}>Open the entry</PageLink></span></blockquote>
            ))}
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}

/** The records behind an intent (or an outcome), newest first, paged by cursor. */
function RecordsPanel({ intentKey, outcome, filters, onBack, onOpen }: { intentKey: string | null; outcome: string | null; filters: PulseFilters; onBack: () => void; onOpen: (askId: string) => void }) {
  const params = { ...filterParams(filters), intentKey: intentKey ?? undefined, outcome: outcome ?? undefined };
  const list = usePagedList((cursor) => api.pulseRecords({ ...params, cursor, limit: 50 }), JSON.stringify(params));
  const columns: Array<Column<PulseRecord>> = [
    { key: 'when', header: 'When', cell: (row) => whenText(row.ts) },
    { key: 'surface', header: 'Surface', muted: true, hideOnPhone: true, cell: (row) => surfaceLabel(row.surface) },
    { key: 'domain', header: 'Domain', hideOnPhone: true, cell: (row) => domainLabel(row.domain) },
    { key: 'intent', header: 'Intent', cell: (row) => row.intent },
    { key: 'outcome', header: 'Outcome', cell: (row) => <Chip spec={outcomeChip(row.outcome)} /> },
    { key: 'reason', header: 'Why', muted: true, hideOnPhone: true, cell: (row) => reasonLabel(row.outcomeReason) },
    { key: 'tools', header: 'Tools', numeric: true, hideOnPhone: true, cell: (row) => count(row.toolCalls.length) },
    { key: 'cost', header: 'Cost', numeric: true, cell: (row) => usdText(row.costUsd) },
  ];
  const what = intentKey ? `“${intentKey.replace(/-/g, ' ')}”` : outcome ? `outcome ${outcomeChip(outcome).label}` : 'every ask';
  return (
    <Panel
      flush
      title="Records"
      description={list.load.state === 'ready' ? `${count(list.total)} records for ${what}, newest first` : `The records for ${what}`}
      actions={<Button size="sm" variant="ghost" icon={ArrowLeft} onClick={onBack}>Back to intents</Button>}
      footer={<span>{STORED_TEXT_NOTE}</span>}
    >
      {list.load.state === 'failed' ? <div className="ui-panel-pad"><PulseError title="The records could not be loaded" error={list.load.error} onRetry={list.reload} retrying={list.reloading} /></div> : (
        <Table
          label="Records"
          columns={columns}
          rows={list.items}
          rowKey={(row) => row.askId}
          loading={list.load.state === 'loading'}
          skeletonRows={6}
          onRowClick={(row) => onOpen(row.askId)}
          more={{ cursor: list.next, loading: list.moreLoading, onMore: list.loadMore }}
          empty={<EmptyState compact icon={MessagesSquare} title="No records in this range">Widen the date range, or clear the filters.</EmptyState>}
        />
      )}
    </Panel>
  );
}

const SIGNAL_WORDS: Record<string, string> = {
  claimedFileNoneSaved: 'claimed a file, none saved', repeatInEpisode: 'asked again in the same conversation', rephraseInEpisode: 'rephrased next',
  abandonedEpisode: 'conversation abandoned after it', refusalStop: 'the model refused', pendingExpired: 'a pending action expired', codeNonZeroExit: 'code exited non-zero',
  knowledgeNoSource: 'knowledge ask with no source', likelyCompanyQuestion: 'World IQ ask that looks like a company question',
};

/** One record, the whole file: the facts, the tools and actions, the signals, the scrubbed question and answer. */
export function RecordDrawer({ askId, onClose }: { askId: string | null; onClose: () => void }) {
  const { load, reload, reloading } = useLoad<PulseRecord | null>(() => (askId ? api.pulseRecord(askId) : Promise.resolve(null)), askId ?? '');
  const record = load.state === 'ready' ? load.data : null;
  const signals = record ? Object.entries(record.signals).filter(([, on]) => on).map(([key]) => SIGNAL_WORDS[key] ?? key) : [];
  return (
    <Drawer
      open={askId !== null}
      onClose={onClose}
      title={record ? record.intent : 'Record'}
      description={record ? `${whenText(record.ts)} · ${surfaceLabel(record.surface)} · ${domainLabel(record.domain)}` : 'Loading…'}
      className="ui-drawer--wide"
      footer={<AnonymousFoot what="record" />}
    >
      {load.state === 'loading' ? <div className="ui-form" aria-busy="true"><Skeleton size="lg" /><Skeleton size="lg" /><Skeleton size="xl" /></div> : null}
      {load.state === 'failed' ? <PulseError title="The record could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /> : null}
      {record ? (
        <div className="ui-form">
          <Facts rows={[
            ['Outcome', <span className="ui-chips"><Chip spec={outcomeChip(record.outcome)} />{record.outcomeReason ? <StatusChip tone="neutral">{reasonLabel(record.outcomeReason)}</StatusChip> : null}<span className="ui-quiet">by {record.outcomeBy}{record.saidBy ? ` · ${record.saidBy.replace(/-/g, ' ')}` : ''}{record.judgeConfidence !== null ? ` · judge ${Math.round(record.judgeConfidence * 100)} %` : ''}</span></span>],
            ['Model', <span>{record.model} · {count(record.rounds)} rounds · {usdText(record.costUsd)} · stop {record.stopReason}{record.judgeModel ? ` · judged by ${record.judgeModel}` : ''}</span>],
            ['Time', <span>{durationBucketText(record.durationMs)}{typeof record.ttftMs === 'number' ? ` · first word ${durationBucketText(record.ttftMs)}` : ''}<span className="ui-quiet"> · ranges, never exact times</span></span>],
            ['Tools', record.toolCalls.length ? (
              <ul className="ui-list">{record.toolCalls.map((call, index) => (
                <li key={index}>
                  <code>{`${call.server ? `${call.server}/` : ''}${call.name}`}</code>{` · ${call.decision} · ${call.ok ? 'ok' : `failed${call.reason ? ` (${reasonLabel(call.reason)})` : ''}${call.code ? ` ${call.code}` : ''}`} · ${durationBucketText(call.ms)}`}
                  {call.detail && (call.detail.message || call.detail.rule) ? <span className="ui-quiet">{` — ${[call.detail.rule, call.detail.message].filter(Boolean).join(': ')}`}</span> : null}
                </li>
              ))}</ul>
            ) : 'none'],
            ['Actions', record.actions.length ? (
              <ul className="ui-list">{record.actions.map((action) => <li key={action.key}><code>{action.tool}</code> · {action.status}{action.highProfile ? ' · higher-profile' : ''}{action.settledMs !== null ? ` · settled ${whenText(action.settledMs)}` : ''}{action.errorCode ? ` · ${action.errorCode}` : ''}</li>)}</ul>
            ) : 'none proposed'],
            ['Signals', signals.length ? signals.join(' · ') : 'none'],
            ['Health at the time', record.healthAtTime.length ? <span className="ui-chips">{record.healthAtTime.map((h) => <span key={h.subjectId}><Tag>{subjectName(h.subjectId).name}</Tag> <Chip spec={healthChip(h.state)} /></span>)}</span> : 'every tool healthy'],
            ['Redactions', Object.entries(record.redactions).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(', ') || 'none needed'],
            ['Feedback', record.feedback ? <span>{record.feedback.kind === 'card' ? `card: ${record.feedback.rating ?? '—'}${record.feedback.undo ? ', had to undo it' : ''}` : `thumb ${record.feedback.thumb}`}{record.feedback.tags?.length ? ` · ${record.feedback.tags.join(', ')}` : ''}{record.feedback.comment ? ` · “${record.feedback.comment}”` : ''}</span> : 'none'],
          ]} />
          <div className="ui-section">
            <h3 className="ui-section-title">Ask (scrubbed)</h3>
            <p className="ui-para ui-quiet">{STORED_TEXT_NOTE}</p>
            <p className="pulse-text">{record.questionText ?? (record.textErasedAt ? `Erased ${whenText(record.textErasedAt)}.` : 'Not kept.')}</p>
          </div>
          <div className="ui-section">
            <h3 className="ui-section-title">Answer (scrubbed, first 2,000 characters)</h3>
            <p className="pulse-text">{record.answerText ?? (record.textErasedAt ? `Erased ${whenText(record.textErasedAt)}.` : 'Not kept.')}</p>
          </div>
          {record.failureDiagnostics && record.failureDiagnostics.length > 0 ? (
            <div className="ui-section">
              <h3 className="ui-section-title">Failure diagnostics</h3>
              <p className="ui-para ui-quiet">What the PC reported when the tool failed (contract section 15), scrubbed twice: no user, machine or device. The newest three bundles are kept.</p>
              {record.failureDiagnostics.map((bundle, index) => <FailureBundle key={index} bundle={bundle} />)}
            </div>
          ) : null}
        </div>
      ) : null}
    </Drawer>
  );
}

/** One failure bundle: the code and message, the runner's exception and traceback and the log tails collapsed, the versions; Copy takes the whole bundle. */
function FailureBundle({ bundle }: { bundle: PulseFailureBundle }) {
  const [copied, setCopied] = useState<'done' | 'failed' | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(bundleText(bundle));
      setCopied('done');
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      setCopied('failed');
    }
  };
  const runner = bundle.runner;
  const foot = bundleFootText(bundle);
  return (
    <div className="pulse-bundle">
      <div className="ui-actions">
        <strong><code>{bundleTitle(bundle)}</code></strong>
        <span className="ui-quiet">{whenText(bundle.atHour)}</span>
        <Button size="sm" icon={copied === 'done' ? Check : Copy} onClick={() => void copy()} title="Copy the whole bundle, the collapsed parts included">{copied === 'done' ? 'Copied' : 'Copy'}</Button>
      </div>
      <p className="pulse-text">{bundle.message}</p>
      {runner?.exception || runner?.traceback ? (
        <details className="ui-details">
          <summary>{`Runner exception${runner.mode ? ` · ${runner.mode}` : ''}${runner.title ? ` · ${runner.title}` : ''}`}</summary>
          <div className="ui-details-body">
            {runner.exception ? <p className="pulse-text">{runner.exception}</p> : null}
            {runner.traceback ? <pre className="ui-pre">{runner.traceback}</pre> : null}
          </div>
        </details>
      ) : null}
      {runner?.warnings?.length ? (
        <div>
          <p className="ui-para ui-quiet">{`Warnings (${runner.warnings.length}):`}</p>
          <ul className="ui-list">{runner.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
        </div>
      ) : null}
      {bundle.serverStderrTail?.length ? (
        <details className="ui-details">
          <summary>{`MCP server stderr, last ${bundle.serverStderrTail.length} ${bundle.serverStderrTail.length === 1 ? 'line' : 'lines'}`}</summary>
          <div className="ui-details-body"><pre className="ui-pre">{bundle.serverStderrTail.join('\n')}</pre></div>
        </details>
      ) : null}
      <p className="pulse-foot">{`${versionsText(bundle.versions)}${foot ? ` · ${foot}` : ''}`}</p>
      {copied === 'failed' ? <p className="pulse-foot">The bundle could not be copied; select its text instead.</p> : null}
    </div>
  );
}
